import {
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
  type ShopifyAnalyticsProduct,
  type ShopifyPageViewPayload,
  ShopifySalesChannel,
} from "@shopify/hydrogen-react";
import { BaseAdapter } from "./base-adapter";
import type { TEvent } from "../types";
import { EventType } from "../types";

// Constants based on environment variables
const DEFAULT_CURRENCY = "INR";
const DEFAULT_LANGUAGE = "en";

/**
 * Type definitions for analytics payloads
 */
type SendPageViewPayload = {
  pageType?: string;
  products?: ShopifyAnalyticsProduct[];
  collectionHandle?: string;
  searchString?: string;
  totalValue?: number;
  cartId?: string;
};

type SendAddToCartPayload = {
  cartId: string;
  products?: ShopifyAnalyticsProduct[];
  totalValue?: ShopifyPageViewPayload["totalValue"];
};

/**
 * Configuration interface for Shopify Analytics Adapter
 */
export interface ShopifyAdapterConfig {
  shopId: string;
  domain: string;
  currency?: string;
}

/**
 * Shopify Analytics Adapter for tracking events to Shopify Admin Analytics
 */
export class ShopifyAdapter extends BaseAdapter {
  public readonly name = "ShopifyAnalytics";
  private shopId: string;
  private domain: string;
  private currency: string;

  constructor(config: ShopifyAdapterConfig) {
    super(config);
    this.shopId = config.shopId;
    this.domain = config.domain;
    this.currency = config.currency || DEFAULT_CURRENCY;
  }

  /**
   * Initialize the Shopify Analytics adapter
   */
  public async initialize(): Promise<void> {
    this.initializeLogger();

    try {
      // Enable Shopify cookies for attribution tracking
      if (typeof window !== "undefined") {
        this.logger.info("Shopify Analytics adapter initialized successfully", {
          shopId: this.shopId,
          domain: this.domain,
          currency: this.currency,
        });

        // Note: useShopifyCookies should be called at the React component level
        // This will be handled in the ShopkitAnalytics component
      }
      this.initialized = true;
    } catch (error) {
      this.logger.error(
        "Failed to initialize Shopify Analytics",
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  }

  /**
   * Convert product ID to Shopify GID format if it's not already
   */
  private formatProductGid(productId: string): string {
    return productId.startsWith("gid://shopify/Product/")
      ? productId
      : `gid://shopify/Product/${productId}`;
  }

  /**
   * Get user consent status from your consent management system
   */
  private getUserConsent() {
    // Check if user has given consent (implement based on your consent system)
    const hasConsent = this.checkUserConsent();

    return {
      hasUserConsent: hasConsent,
      analyticsAllowed: hasConsent && this.checkAnalyticsConsent(),
      marketingAllowed: hasConsent && this.checkMarketingConsent(),
      saleOfDataAllowed: hasConsent && this.checkSaleOfDataConsent(),
    };
  }

  /**
   * Check user consent (implement based on your consent management)
   */
  private checkUserConsent(): boolean {
    // Example implementations:
    // return localStorage.getItem('user_consent') === 'true';
    // return document.cookie.includes('consent=granted');
    // return window.gtag?.('consent', 'query') === 'granted';

    // For now, return true but implement your actual consent logic
    return true;
  }

  private checkAnalyticsConsent(): boolean {
    // Implement your analytics consent check
    return true;
  }

  private checkMarketingConsent(): boolean {
    // Implement your marketing consent check
    return true;
  }

  private checkSaleOfDataConsent(): boolean {
    // Implement your sale of data consent check
    return true;
  }

  /**
   * Create base payload for all events
   */
  private createBasePayload(): any {
    const consent = this.getUserConsent();

    return {
      ...getClientBrowserParameters(),
      ...consent,
      storefrontId: this.domain,
      shopifySalesChannel: ShopifySalesChannel.headless,
      shopId: `gid://shopify/Shop/${this.shopId}`,
      currency: this.currency,
      acceptedLanguage: DEFAULT_LANGUAGE,
      ...this.enhanceWithAffiliateParams({}),
    };
  }

  /**
   * Send page view event using the enhanced functionality
   */
  public sendPageView = (
    eventName: keyof typeof AnalyticsEventName,
    payload?: SendPageViewPayload
  ) => {
    const enhancedPayload = {
      ...this.createBasePayload(),
      ...payload,
    };

    return sendShopifyAnalytics({
      eventName,
      payload: enhancedPayload,
    });
  };

  /**
   * Send add to cart event using the enhanced functionality
   */
  public sendAddToCart = ({
    cartId,
    totalValue,
    products,
  }: SendAddToCartPayload) => {
    return this.sendPageView(AnalyticsEventName.ADD_TO_CART, {
      cartId,
      totalValue,
      products,
    });
  };

  /**
   * Track an event using Shopify Analytics
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined") {
      this.logger.warn(
        "Shopify Analytics not initialized or not in browser environment"
      );
      return;
    }

    this.logger.debug("Processing Shopify Analytics event", {
      eventType: event.type,
      eventData: event,
    });

    try {
      switch (event.type) {
        case EventType.PAGE_VIEW:
          this.sendPageView(AnalyticsEventName.PAGE_VIEW);
          this.logger.debug("Sent PAGE_VIEW event to Shopify Analytics");
          break;

        case EventType.VIEW_CONTENT:
          this.sendPageView(AnalyticsEventName.PRODUCT_VIEW);
          this.logger.debug("Sent PRODUCT_VIEW event to Shopify Analytics");
          break;

        case EventType.ADD_TO_CART:
          this.trackAddToCart(event);
          break;

        case EventType.CHECKOUT_STARTED:
        case EventType.BEGIN_CHECKOUT:
          // For checkout events, send as PAGE_VIEW with checkout page context
          this.sendPageView(AnalyticsEventName.PAGE_VIEW, {
            pageType: "checkout",
          });
          this.logger.debug(
            "Sent checkout PAGE_VIEW event to Shopify Analytics"
          );
          break;

        default:
          this.logger.debug("Event not supported by Shopify Analytics", {
            eventType: event.type,
          });
          break;
      }
    } catch (error) {
      this.logger.error(
        "Error tracking Shopify Analytics event",
        error instanceof Error ? error : new Error(String(error)),
        { eventType: event.type }
      );
    }
  }

  /**
   * Check if Shopify session is properly established
   */
  public isSessionValid(): boolean {
    if (typeof window === "undefined") return false;

    const shopifyY = document.cookie.includes("_shopify_y=");
    const shopifyS = document.cookie.includes("_shopify_s=");

    return shopifyY && shopifyS;
  }

  /**
   * Get session debug information
   */
  public getSessionDebugInfo(): object {
    if (typeof window === "undefined")
      return { error: "Not in browser environment" };

    return {
      sessionValid: this.isSessionValid(),
      cookies: {
        shopify_y: document.cookie.includes("_shopify_y="),
        shopify_s: document.cookie.includes("_shopify_s="),
        all: document.cookie,
      },
      shopId: this.shopId,
      initialized: this.initialized,
    };
  }

  /**
   * Track add to cart event with enhanced error handling
   */
  private trackAddToCart(event: any): void {
    if (!event.productId) {
      this.logger.warn("ADD_TO_CART event missing productId");
      return;
    }

    try {
      const productGid = this.formatProductGid(event.productId);
      const cartKey = `merchant_${process.env.NEXT_PUBLIC_MERCHANT_NAME}_cartId`;
      const cartId = localStorage.getItem(cartKey) || "";

      if (!cartId) {
        this.logger.warn("No cart ID found, creating anonymous session");
        // You might want to generate a temporary cart ID or handle this case
      }

      // Validate product data
      if (!event.price || event.price <= 0) {
        this.logger.warn("Invalid or missing price for ADD_TO_CART", {
          price: event.price,
        });
      }

      // Create products array for analytics
      const productPrice = Math.max(0, event.price || 0);
      const productQuantity = Math.max(1, event.quantity || 1);

      const products: ShopifyAnalyticsProduct[] = [
        {
          productGid: productGid,
          variantGid:
            event.variantId || productGid.replace("Product", "ProductVariant"),
          quantity: productQuantity,
          price: productPrice.toString(), // Convert to string as required by ShopifyAnalyticsProduct
          name: event.productName || "Unknown Product",
          brand: event.brand || "Unknown Brand",
        },
      ];

      const totalValue = productPrice * productQuantity;

      this.logger.debug("ADD_TO_CART event tracked successfully", {
        productGid,
        quantity: productQuantity,
        price: productPrice,
        totalValue,
      });

      // Use the enhanced sendAddToCart method
      this.sendAddToCart({
        cartId,
        products,
        totalValue,
      });
    } catch (error) {
      this.logger.error(
        "Error in trackAddToCart",
        error instanceof Error ? error : new Error(String(error))
      );
      // Don't throw - just log the error to prevent breaking the user experience
    }
  }
}
